/* eslint-disable @typescript-eslint/no-explicit-any */
interface EventsMap {
  [event: string]: any;
}

interface DefaultEvents extends EventsMap {
  [event: string]: (...args: any[]) => void;
}

/**
 * This flag is true if execution following an emit should be immediately stopped.
 * Useful for any before-* events that prevent continued processes
 */
export type TPreventExecution = boolean;

export interface Unsubscribe {
  (): void;
}

/**
 * Create event emitter.
 *
 * ```js
 * import { EventEmitter } from 'utils/events'
 *
 * class Ticker {
 *   constructor() {
 *     this.emitter = new EventEmitter()
 *   }
 *   on(...args) {
 *     return this.emitter.on(...args)
 *   }
 *   tick() {
 *     this.emitter.emit('tick')
 *   }
 * }
 * ```
 */
export class EventEmitter<Events extends EventsMap = DefaultEvents> {
  /**
   * Event names in keys and arrays with listeners in values.
   *
   * ```js
   * emitter1.events = emitter2.events
   * emitter2.events = { }
   * ```
   */
  events: Partial<{ [E in keyof Events]: Events[E][] }> = {};

  /**
   * Add a listener for a given event.
   *
   * ```js
   * const unbind = ee.on('tick', (tickType, tickDuration) => {
   *   count += 1
   * })
   *
   * disable () {
   *   unbind()
   * }
   * ```
   *
   * @param event The event name.
   * @param cb The listener function.
   * @returns Unbind listener from event.
   */
  on<K extends keyof Events>(this: this, event: K, cb: Events[K]): Unsubscribe {
    this.events[event] = this.events[event] || [];

    this.events[event]?.push(cb);

    return () => (this.events[event] = (this.events[event] || [])?.filter((callback) => callback !== cb));
  }

  /**
   * Calls each of the listeners registered for a given event.
   *
   * ```js
   * ee.emit('tick', tickType, tickDuration)
   * ```
   *
   * @param event The event name.
   * @param args The arguments for listeners.
   */
  emit<K extends keyof Events>(
    this: this,
    event: K,
    ...args: Parameters<Events[K]>
  ): [TPreventExecution, unknown[], Events[K][] | undefined, Parameters<Events[K]>] {
    const callbacks = this.events[event];
    let callbackResults = [];
    if (Array.isArray(callbacks)) {
      callbackResults = callbacks.map((callback) => callback(...args));
    }
    const anyCallbackReturnedFalse = callbackResults.find((res) => res === false) === false;
    return [anyCallbackReturnedFalse, callbackResults, callbacks, args];
  }

  /**
   * Add a one-time listener for a given event.
   *
   * ```js
   * ee.once('tick', (tickType, tickDuration) => {
   *   count += 1
   * })
   * ```
   *
   * @param event The event name.
   * @param cb The listener function.
   * @returns Unbind listener from event.
   */
  once<K extends keyof Events>(this: this, event: K, cb: Events[K]): Unsubscribe {
    const unbind = this.on(event, ((...args: Parameters<Events[K]>) => {
      cb(...args);
      unbind();
    }) as Events[K]);
    return unbind;
  }

  destroy(): void {
    // remove all events
    this.events = {};
  }
}
